import { useCallback, useEffect, useRef } from "react";

/** Auto sign-out after this many seconds of inactivity. */
export const DEFAULT_IDLE_TIMEOUT_SECONDS = 15 * 60; // 15 minutes
export const IDLE_ACTIVITY_STORAGE_KEY = "auth:idle:last-activity";

/** Events that indicate user activity. */
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  "mousedown",
  "mousemove",
  "pointerdown",
  "keydown",
  "scroll",
  "wheel",
  "touchstart",
  "click",
];

interface UseIdleTimeoutOptions {
  /** Idle timeout in seconds (default: 900 = 15 minutes). */
  timeoutSeconds?: number;
  /** Warning period before idle logout (default: 120 seconds). */
  warningSeconds?: number;
  /** Callback fired when the warning period begins. */
  onWarning?: () => void;
  /** Callback fired when activity resets the timer. */
  onActivity?: () => void;
  /** Callback fired when the user becomes idle. */
  onIdle: () => void;
  /** Whether the timeout is enabled (default: true). */
  enabled?: boolean;
}

interface UseIdleTimeoutResult {
  reset: () => void;
}

/**
 * Tracks user activity and fires `onIdle` after a period of inactivity. The
 * timer resets on pointer/mouse, keyboard, scroll, touch, wheel, and click
 * events. A warning callback fires before the final idle callback.
 */
export function useIdleTimeout({
  timeoutSeconds = DEFAULT_IDLE_TIMEOUT_SECONDS,
  warningSeconds = 2 * 60,
  onWarning,
  onActivity,
  onIdle,
  enabled = true,
}: UseIdleTimeoutOptions): UseIdleTimeoutResult {
  const RESET_THROTTLE_MS = 250;
  const warningTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningActiveRef = useRef(false);
  const onIdleRef = useRef(onIdle);
  const onWarningRef = useRef(onWarning);
  const onActivityRef = useRef(onActivity);
  const lastResetRef = useRef(0);
  const lastActivityAtRef = useRef(0);
  const startTimerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  useEffect(() => {
    onWarningRef.current = onWarning;
  }, [onWarning]);

  useEffect(() => {
    onActivityRef.current = onActivity;
  }, [onActivity]);

  const clearTimer = useCallback(() => {
    if (warningTimeoutIdRef.current !== null) {
      clearTimeout(warningTimeoutIdRef.current);
      warningTimeoutIdRef.current = null;
    }
    if (idleTimeoutIdRef.current !== null) {
      clearTimeout(idleTimeoutIdRef.current);
      idleTimeoutIdRef.current = null;
    }
  }, []);

  const parseActivityTimestamp = useCallback((value: string | null): number | null => {
    if (value == null) {
      return null;
    }

    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return null;
    }

    return parsed;
  }, []);

  const readSharedActivityTimestamp = useCallback((): number | null => {
    try {
      return parseActivityTimestamp(window.localStorage.getItem(IDLE_ACTIVITY_STORAGE_KEY));
    } catch {
      return null;
    }
  }, [parseActivityTimestamp]);

  const persistSharedActivityTimestamp = useCallback((timestamp: number) => {
    try {
      window.localStorage.setItem(IDLE_ACTIVITY_STORAGE_KEY, String(timestamp));
    } catch {
      // Browsers may deny storage writes in hardened modes; keep per-tab timers.
    }
  }, []);

  const resolveLatestActivityTimestamp = useCallback(() => {
    const sharedTimestamp = readSharedActivityTimestamp();
    if (sharedTimestamp != null && sharedTimestamp > lastActivityAtRef.current) {
      lastActivityAtRef.current = sharedTimestamp;
    }
    return lastActivityAtRef.current;
  }, [readSharedActivityTimestamp]);

  const startTimer = useCallback(() => {
    clearTimer();
    warningActiveRef.current = false;
    const now = Date.now();
    const latestActivityTimestamp = resolveLatestActivityTimestamp();
    const warningThresholdMs = Math.max(0, timeoutSeconds - warningSeconds) * 1000;
    const idleThresholdMs = timeoutSeconds * 1000;
    const warningAt = latestActivityTimestamp + warningThresholdMs;
    const idleAt = latestActivityTimestamp + idleThresholdMs;
    const warningDelayMs = Math.max(0, warningAt - now);
    const idleDelayMs = Math.max(0, idleAt - now);

    warningTimeoutIdRef.current = setTimeout(() => {
      const newestActivityTimestamp = resolveLatestActivityTimestamp();
      const elapsedSinceActivityMs = Date.now() - newestActivityTimestamp;
      if (elapsedSinceActivityMs < warningThresholdMs) {
        startTimerRef.current?.();
        return;
      }

      warningActiveRef.current = true;
      onWarningRef.current?.();
    }, warningDelayMs);

    idleTimeoutIdRef.current = setTimeout(() => {
      const newestActivityTimestamp = resolveLatestActivityTimestamp();
      const elapsedSinceActivityMs = Date.now() - newestActivityTimestamp;
      if (elapsedSinceActivityMs < idleThresholdMs) {
        startTimerRef.current?.();
        return;
      }

      onIdleRef.current();
    }, idleDelayMs);
  }, [clearTimer, resolveLatestActivityTimestamp, timeoutSeconds, warningSeconds]);

  useEffect(() => {
    startTimerRef.current = startTimer;
    return () => {
      startTimerRef.current = null;
    };
  }, [startTimer]);

  const reset = useCallback(() => {
    if (!enabled) return;
    warningActiveRef.current = false;
    lastResetRef.current = Date.now();
    lastActivityAtRef.current = lastResetRef.current;
    persistSharedActivityTimestamp(lastResetRef.current);
    startTimer();
  }, [enabled, persistSharedActivityTimestamp, startTimer]);

  useEffect(() => {
    if (!enabled) {
      warningActiveRef.current = false;
      clearTimer();
      return;
    }

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastResetRef.current < RESET_THROTTLE_MS) return;
      lastResetRef.current = now;
      lastActivityAtRef.current = now;
      persistSharedActivityTimestamp(now);
      onActivityRef.current?.();
      startTimer();
    };

    const now = Date.now();
    const sharedActivityTimestamp = readSharedActivityTimestamp();
    const initialActivityTimestamp =
      sharedActivityTimestamp != null
        ? Math.max(sharedActivityTimestamp, now)
        : now;
    lastResetRef.current = initialActivityTimestamp;
    lastActivityAtRef.current = initialActivityTimestamp;
    persistSharedActivityTimestamp(initialActivityTimestamp);
    startTimer();

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    const handleStorage = (event: StorageEvent) => {
      if (event.storageArea !== window.localStorage) {
        return;
      }
      if (event.key !== IDLE_ACTIVITY_STORAGE_KEY) {
        return;
      }

      const sharedActivity = parseActivityTimestamp(event.newValue);
      if (sharedActivity == null || sharedActivity <= lastActivityAtRef.current) {
        return;
      }

      lastResetRef.current = sharedActivity;
      lastActivityAtRef.current = sharedActivity;
      onActivityRef.current?.();
      startTimer();
    };

    window.addEventListener("storage", handleStorage);

    return () => {
      clearTimer();
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      window.removeEventListener("storage", handleStorage);
    };
  }, [
    clearTimer,
    enabled,
    parseActivityTimestamp,
    persistSharedActivityTimestamp,
    readSharedActivityTimestamp,
    startTimer,
  ]);

  return { reset };
}

export default useIdleTimeout;
